import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Spinner,
  Typography,
  WithToolTip,
} from "@atlas/core";
import { hasAuthToken, useTranslation } from "@atlas/sdk";
import type { IHubSubscribedSolution } from "@models";
import { ExternalLink } from "lucide-react";
import { memo } from "react";

interface ISolutionLaunchCardProps {
  solutionData: IHubSubscribedSolution;
  title: string;
  launchUrl: string;
  launchDisabled: boolean;
  isLaunching?: boolean;
  onLaunchApp: (launchUrl: string, solutionId?: string) => void;
}

function SolutionFeatureBadges({
  solutionData,
  t,
}: {
  solutionData: IHubSubscribedSolution;
  t: (key: string, options?: { defaultValue?: string }) => string;
}) {
  const badges = [
    solutionData.idpOutputEnabled &&
      t("hub.home.solution_badge_idp_output", { defaultValue: "IDP Output" }),
    solutionData.idpOutputBackupEnabled &&
      t("hub.home.solution_badge_idp_backup", { defaultValue: "IDP Backup" }),
    solutionData.hasPhysicalAddOn &&
      t("hub.home.solution_badge_physical", {
        defaultValue: "Physical Add-on",
      }),
    solutionData.hasROTAddOn &&
      t("hub.home.solution_badge_rot", { defaultValue: "ROT Add-on" }),
    solutionData.ocrEnabled &&
      t("hub.home.solution_badge_ocr", { defaultValue: "OCR" }),
  ].filter((label): label is string => Boolean(label));

  if (!badges.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((label) => (
        <Badge key={label} variant="outline" size="sm">
          {label}
        </Badge>
      ))}
    </div>
  );
}

export const SolutionLaunchCard = memo(function SolutionLaunchCard({
  solutionData,
  title,
  launchUrl,
  launchDisabled,
  isLaunching,
  onLaunchApp,
}: ISolutionLaunchCardProps) {
  const { t } = useTranslation();

  const trimmedDescription = solutionData.description?.trim() ?? "";
  const authorDisplay =
    solutionData.subscribeUserName?.trim() ||
    solutionData.createdBy?.trim() ||
    solutionData.subscribeUserId?.trim() ||
    "";

  return (
    <Card className="h-full min-w-0 overflow-hidden">
      <CardHeader className="min-w-0">
        <WithToolTip tooltipText={title} className="min-w-0 flex-1">
          <CardTitle className="truncate">{title}</CardTitle>
        </WithToolTip>
      </CardHeader>

      <CardContent className="flex flex-col gap-2.5">
        {trimmedDescription ? (
          <WithToolTip
            tooltipText={trimmedDescription}
            className="w-full min-w-0">
            <Typography.P className="mt-0 mb-0 line-clamp-2 min-h-10 text-sm leading-5 text-foreground">
              {trimmedDescription}
            </Typography.P>
          </WithToolTip>
        ) : null}

        {authorDisplay ? (
          <div className="flex min-w-0 items-center">
            <WithToolTip tooltipText={authorDisplay} className="min-w-0 flex-1">
              <Typography.Muted className="truncate">
                <span>
                  {t("hub.home.solution_by_label", {
                    defaultValue: "by",
                  })}{" "}
                </span>
                <span>{authorDisplay}</span>
              </Typography.Muted>
            </WithToolTip>
          </div>
        ) : null}

        <SolutionFeatureBadges solutionData={solutionData} t={t} />
      </CardContent>

      <CardFooter className="justify-between gap-3">
        <Badge variant="secondary" size="sm">
          {t("hub.home.solution_type_badge", { defaultValue: "Solution" })}
        </Badge>

        <Button
          size="xs"
          variant="default"
          disabled={launchDisabled && !isLaunching}
          loading={isLaunching}
          onClick={(event) => {
            event.preventDefault();
            if (!hasAuthToken()) {
              window.dispatchEvent(new CustomEvent("session-expired"));
              return;
            }
            onLaunchApp(launchUrl, solutionData.solutionId);
          }}>
          {t("hub.launch.launch_button", { defaultValue: "Launch" })}
          {isLaunching ? (
            <Spinner className="size-3.5 text-current" />
          ) : (
            <ExternalLink />
          )}
        </Button>
      </CardFooter>
    </Card>
  );
});
